import * as turf from "@turf/turf";
import type mapboxgl from "mapbox-gl";
import { useEffect } from "react";
import { flattenToPoints } from "../utils/flattenToPoints";

interface UseHeatMapProps {
  map: mapboxgl.Map | null;
  geoData: mapboxgl.MapboxGeoJSONFeature | mapboxgl.GeoJSONSourceRaw | null;
  type: "heatmap" | "hexabin";
  cellSize?: number;
}

const defaultCellSize = 1;

const extractCoordinates = (geoData) => {
  const coordinates = [];
  turf.coordEach(geoData, (coord) => {
    coordinates.push(coord);
  });
  return coordinates;
};

const coordinatesToPointsFeatureCollection = (coordinates) => {
  const pointFeatures = coordinates.map((coord) => turf.point(coord));
  return turf.featureCollection(pointFeatures);
};

function countCoordinatesInFeatureCollection(featureCollection) {
  let coordinateCount = 0;

  featureCollection.features.forEach((feature) => {
    if (feature.geometry) {
      const geometryType = feature.geometry.type;

      switch (geometryType) {
        case "Point":
        case "MultiPoint":
          coordinateCount += 1;
          break;

        case "LineString":
        case "MultiLineString":
          coordinateCount += feature.geometry.coordinates.length;
          break;

        case "Polygon":
        case "MultiPolygon":
          feature.geometry.coordinates.forEach((polygonCoordinates) => {
            coordinateCount += polygonCoordinates.length;
          });
          break;

        default:
          break;
      }
    }
  });

  return coordinateCount;
}

const useHeatMap = ({
  map,
  geoData,
  type,
  cellSize = defaultCellSize,
  mapbounds,
  heatmapStyle,
  setLabelsData,
}: UseHeatMapProps) => {
  useEffect(() => {
    const processHeatmap = async () => {
      if (map?._loaded && geoData) {
        if (heatmapStyle === "drawn") {
          setLabelsData(geoData);
        }
        // console.log(heatmapStyle);
        // console.log(geoData);
        const flatGeoData = await flattenToPoints(mapbounds, geoData);

        if (map.getLayer("heatmap-layer")) {
          map.removeLayer("heatmap-layer");
        }

        if (map.getSource("heatmap-source")) {
          map.removeSource("heatmap-source");
        }

        if (type === "hexabin") {
          const bbox = turf.bbox(mapbounds);
          const mapboundsBbox = turf.bbox(mapbounds);
          const mapboundsBboxPolygon = turf.bboxPolygon(mapboundsBbox);
          const mapboundsArea = turf.area(mapboundsBboxPolygon);
          const dynamicSpacing = Math.sqrt(mapboundsArea) / 30000;

          const cellSizeKM = dynamicSpacing || 0.1;
          const hexGrid = turf.hexGrid(bbox, cellSizeKM, {
            mask: mapbounds?.features?.[0],
          });

          // Extract coordinates from your original geoData
          const coordinates = extractCoordinates(flatGeoData);

          // Convert the extracted coordinates to a FeatureCollection of Point Features
          const pointFeatures =
            coordinatesToPointsFeatureCollection(coordinates);
          let maxDensity = 0; // to keep track of the max density
          hexGrid.features.forEach((hex, index, arr) => {
            const pointsWithin = turf.pointsWithinPolygon(pointFeatures, hex);
            const density = pointsWithin.features.length;

            if (density > maxDensity) {
              maxDensity = density; // Update maxDensity if a higher density is found
            }

            arr[index].properties = {
              density,
            };
          });

          map.addSource("heatmap-source", {
            type: "geojson",
            data: hexGrid,
          });

          map.addLayer({
            id: "heatmap-layer",
            type: "fill",
            source: "heatmap-source",
            layout: {},
            paint: {
              "fill-color": [
                "interpolate",
                ["linear"],
                ["get", "density"], // Use the 'density' property of each feature
                0,
                "rgba(33,102,172,0)",
                maxDensity * 0.1,
                "rgb(178,24,43)",
                maxDensity * 0.2,
                "rgb(178,24,43)",
                maxDensity * 0.3,
                "rgb(178,24,43)",
                maxDensity * 0.4,
                "rgb(178,24,43)",
                maxDensity * 0.5,
                "rgb(178,24,43)",
                maxDensity * 0.6,
                "rgb(178,24,43)",
                maxDensity * 0.7,
                "rgb(178,24,43)",
                maxDensity * 0.8,
                "rgb(178,24,43)",
                maxDensity * 0.9,
                "rgb(178,24,43)",
                maxDensity,
                "rgb(178,24,43)",
              ],
              "fill-opacity": [
                "interpolate",
                ["linear"],
                ["get", "density"], // Use the 'density' property of each feature
                0,
                0.2,
                maxDensity,
                0.8,
              ],
            },
          });
        } else if (type === "heatmap") {
          map.addSource("heatmap-source", {
            type: "geojson",
            data: flatGeoData,
          });

          const calculateFactor = (numberOfCoordinates) => {
            // console.log("numberOfCoordinates", numberOfCoordinates);

            if (numberOfCoordinates < 5000) {
              return 1;
            }
            if (numberOfCoordinates < 10000) {
              return 0.7;
            }
            if (numberOfCoordinates < 20000) {
              return 0.3;
            }
            if (numberOfCoordinates < 50000) {
              return 0.3;
            }
            if (numberOfCoordinates < 100000) {
              return 0.3;
            }
            if (numberOfCoordinates < 200000) {
              return 0.27;
            }
            if (numberOfCoordinates > 200000) {
              return 0.25;
            }

            return 1;
          };

          const numberOfCoordinates =
            countCoordinatesInFeatureCollection(flatGeoData);

          const factor = calculateFactor(numberOfCoordinates);
          const finalIntensity = (heatmapStyle?.intensity * factor) / 1000;
          const finalRadius = Number(heatmapStyle?.intensity || 0.1);

          map.addLayer({
            id: "heatmap-layer",
            type: "heatmap",
            source: "heatmap-source",
            paint: {
              "heatmap-opacity": 0.7,
              "heatmap-radius": [
                "interpolate",
                ["linear"],
                ["zoom"],
                0,
                finalRadius * 0.1,
                12,
                finalRadius * 0.5,
                18,
                finalRadius * 2,
              ],
              "heatmap-intensity": [
                "interpolate",
                ["linear"],
                ["zoom"],
                0,
                finalIntensity * 0.1,
                9,
                finalIntensity * 0.5,
                18,
                finalIntensity * 8,
              ],
              "heatmap-color": [
                "interpolate",
                ["linear"],
                ["heatmap-density"],
                0,
                "rgba(255,208,97,0)",
                0.1,
                "rgba(255,208,97,0.25)",
                0.2,
                "rgba(255,208,97,0.5)",
                0.3,
                "rgba(255,192,83,0.6)",
                0.4,
                "rgba(255,182,70,0.7)",
                0.5,
                "rgba(255,170,57,0.8)",
                0.6,
                "rgba(255,157,44,0.8)",
                0.7,
                "rgba(247,133,33,0.85)",
                0.8,
                "rgba(239,101,29,0.9)",
                0.9,
                "rgba(208,52,36,0.95)",
                1,
                "rgba(178,24,43,1)",
              ],
            },
          });
        } else if (type === " ") {
          if (map.getLayer("heatmap-layer")) {
            map.removeLayer("heatmap-layer");
          }
          if (map.getSource("heatmap-source")) {
            map.removeSource("heatmap-source");
          }
        }
      }
    };

    processHeatmap();

    return () => {
      if (map) {
        if (map.getLayer("heatmap-layer")) {
          map.removeLayer("heatmap-layer");
        }
        if (map.getSource("heatmap-source")) {
          map.removeSource("heatmap-source");
        }
      }
    };
  }, [map?._loaded, geoData, type, cellSize, heatmapStyle]);
};

export default useHeatMap;
